import Image from 'next/image';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Accordion, Button, Card, Container, Link, Text } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { LinkVariant } from '@/atoms/link/types';
import { ContactCta, FaqBlock } from '@/molecules';

import type { FunctionComponent } from 'react';
import type { TypePrefooterProps } from './types';

/**
 * Default = layout legacy (boxed/full según `footerStyle`).
 * `composedLayout` (toggle en Strapi) = rediseño Figma HoM:
 * `FaqBlock` + `ContactCta`.
 */
export const Prefooter: FunctionComponent<TypePrefooterProps> = ({
  className = '',
  eyebrow,
  title,
  subtitle,
  description,
  image,
  imageShapeUrl,
  accordions,
  link,
  card,
  cta,
  composedLayout = false,
  contactOnly = false,
  footerStyle,
  eyebrowStyleClass,
  titleStyleClass,
  subtitleStyleClass,
  descriptionStyleClass,
  derechaTitleStyleClass,
  derechaDescriptionStyleClass,
  accordionTitleStyleClass,
  accordionDescriptionStyleClass,
  linkStyleClass,
}: TypePrefooterProps) => {
  if (composedLayout) {
    return (
      <div className={`flex flex-col gap-12 lg:gap-20 ${className}`}>
        {!contactOnly && (
          <Container wrapper>
            <FaqBlock
              eyebrow={eyebrow}
              title={title}
              subtitle={subtitle}
              image={image}
              imageShapeUrl={imageShapeUrl}
              accordions={accordions}
              link={
                link?.href ? { label: link.label, href: link.href } : undefined
              }
              eyebrowStyleClass={eyebrowStyleClass}
              titleStyleClass={titleStyleClass}
              subtitleStyleClass={subtitleStyleClass}
              accordionTitleStyleClass={accordionTitleStyleClass}
              accordionDescriptionStyleClass={accordionDescriptionStyleClass}
              linkStyleClass={linkStyleClass}
            />
          </Container>
        )}

        {(cta?.title || cta?.button?.label) && (
          // Ancla propia del banner de contacto para scroll links desde CMS
          // (distinta del #faqs-contact que envuelve todo el prefooter).
          <div id="faqs-contact-banner">
            <ContactCta
              title={cta.title}
              subtitle={cta.subtitle}
              backgroundImage={cta.backgroundImage}
              button={cta.button}
              titleStyleClass={derechaTitleStyleClass}
              subtitleStyleClass={derechaDescriptionStyleClass}
            />
          </div>
        )}
      </div>
    );
  }

  return footerStyle === 'boxed' ? (
    <Container
      wrapper
      className={`py-(--prefooter-py-mobile)! md:py-(--prefooter-py-desktop)! ${className}`}
      /* Las clases de layout van en el u-wrapper: en el Container exterior
         solo envuelven a un único hijo (el propio u-wrapper) y el FAQ + la
         card de contacto se apilaban en vez de ir a dos columnas en lg. */
      classNameWrapper="items-center gap-6 lg:flex-row lg:items-start"
    >
      {!contactOnly && (
        <div className="rounded-custom bg-neutrals-0 w-full px-4 py-6 md:px-16 md:py-10">
          <Text
            className={oneLine`text-headline text-primary-900 mb-4 ${
              titleStyleClass ?? 'text-headline-h3'
            }`}
            as="h2"
          >
            {title}
          </Text>

          <Text
            className={oneLine`text-body text-primary-900 mb-6 ${
              descriptionStyleClass ?? 'text-body-l'
            }`}
            as="p"
          >
            {description}
          </Text>

          <div className="mb-6 flex flex-col gap-y-4">
            {accordions.map((content, index: number) => (
              <Accordion
                title={content.title}
                variant="normal"
                color="white"
                key={index}
                titleStyleClass={accordionTitleStyleClass}
                descriptionStyleClass={accordionDescriptionStyleClass}
              >
                {content.description}
              </Accordion>
            ))}
          </div>

          <Link
            href={link.href}
            className={oneLine`text-primary-500 ${linkStyleClass ?? ''}`}
            icon="right"
            variant={LinkVariant.NEGATIVE}
          >
            {link.label}
          </Link>
        </div>
      )}

      <Card
        className={{
          card: 'rounded-custom bg-neutrals-0 w-full md:max-w-sm',
          content: 'rounded-custom text-body bg-neutrals-0 px-8 py-12',
        }}
        shadow={true}
      >
        <Text
          className={oneLine`fond-bold text-headline text-primary-900 mb-2 ${
            derechaTitleStyleClass ?? 'text-headline-h4'
          }`}
        >
          {card.title}
        </Text>

        {card?.image?.src && (
          <div className={`object-contain' relative h-[220px] w-full`}>
            <Image
              alt={card?.image?.alt ?? 'Imagen de la tarjeta'}
              src={card?.image?.src}
              draggable={false}
              fill
              sizes="(max-width: 768px) 100vw,
                (max-width: 1200px) 50vw,
                33vw"
              className="h-[270px] w-full object-contain p-4"
            />
          </div>
        )}

        <Text
          className={oneLine`text-primary-900 mb-6 text-center ${
            derechaDescriptionStyleClass ?? 'text-body-l'
          }`}
        >
          {card.description}
        </Text>

        <div className="mt-20 flex justify-center">
          <NextLink href={card.link.href} prefetch={false}>
            <Button
              as="span"
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.PRIMARY}
              className="bg-secondary-500! w-max!"
            >
              {card.link.label}
            </Button>
          </NextLink>
        </div>
      </Card>
    </Container>
  ) : (
    <Container
      wrapper
      className={`py-(--prefooter-py-mobile)! md:py-(--prefooter-py-desktop)! ${className}`}
      /* Las clases de layout van en el u-wrapper: en el Container exterior
         solo envuelven a un único hijo (el propio u-wrapper) y el FAQ + la
         card de contacto se apilaban en vez de ir a dos columnas en lg. */
      classNameWrapper="items-center gap-6 lg:flex-row lg:items-start"
    >
      {!contactOnly && (
        <div className="rounded-custom bg-primary-500 w-full px-4 py-6 md:px-16 md:py-10">
          <Text
            className={oneLine`text-headline text-neutrals-0 mb-4 ${
              titleStyleClass ?? 'text-headline-h3'
            }`}
            as="h2"
          >
            {title}
          </Text>

          <Text
            className={oneLine`text-body text-neutrals-0 mb-6 ${
              descriptionStyleClass ?? 'text-body-l'
            }`}
            as="p"
          >
            {description}
          </Text>

          <div className="mb-6 flex flex-col gap-y-4 font-medium">
            {accordions.map((content, index: number) => (
              <Accordion
                title={content.title}
                variant="normal"
                key={index}
                titleStyleClass={accordionTitleStyleClass}
                descriptionStyleClass={accordionDescriptionStyleClass}
              >
                {content.description}
              </Accordion>
            ))}
          </div>

          <Link
            href={link.href}
            icon="right"
            variant={LinkVariant.NEGATIVE}
            className={linkStyleClass}
          >
            {link.label}
          </Link>
        </div>
      )}

      <Card
        className={{
          card: 'rounded-custom bg-neutrals-50 w-full md:max-w-sm',
          content: 'rounded-custom text-body bg-neutrals-50 px-8 py-12',
          image: 'h-[320px] w-full object-contain',
        }}
        image={card.image}
        shadow={false}
      >
        <Text
          className={oneLine`fond-bold text-headline mb-2 ${
            derechaTitleStyleClass ?? 'text-headline-h4'
          }`}
        >
          {card.title}
        </Text>

        <Text
          className={oneLine`mb-6 ${
            derechaDescriptionStyleClass ?? 'text-body-l'
          }`}
        >
          {card.description}
        </Text>

        <NextLink href={card.link.href} prefetch={false}>
          <Button
            as="span"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.SECONDARY}
            className="text-text-neutrals-900 w-max!"
          >
            {card.link.label}
          </Button>
        </NextLink>
      </Card>
    </Container>
  );
};

Prefooter.displayName = 'Prefooter';
